import type { ChannelEntityType, EntityActionType, EntityCanMap, EntityType } from 'shared';
import { allActionsAllowed, computeCan, hierarchy, policyMatrix } from 'shared';
import { useUserStore } from '~/modules/user/user-store';
import type { EnrichableChannel } from '~/query/enrichment/types';

/** Deep compare over the can states (booleans and condition names). */
function hasCanChanged(a: EntityCanMap | undefined, b: EntityCanMap | undefined): boolean {
  if (!a && !b) return false;
  if (!a || !b) return true;

  const aKeys = Object.keys(a) as EntityType[];
  const bKeys = Object.keys(b) as EntityType[];
  if (aKeys.length !== bKeys.length) return true;

  return aKeys.some((key) => {
    const aPerms = a[key];
    const bPerms = b[key];
    if (!aPerms && !bPerms) return false;
    if (!aPerms || !bPerms) return true;
    return (Object.keys(aPerms) as EntityActionType[]).some((action) => aPerms[action] !== bPerms[action]);
  });
}

/** Mirrors the shape of computeCan over the channel type and its descendants, with every action allowed. */
function computeSystemAdminCan(channelType: ChannelEntityType): EntityCanMap {
  const map: EntityCanMap = { [channelType]: { ...allActionsAllowed } };
  for (const descendant of hierarchy.getOrderedDescendants(channelType)) {
    map[descendant] = { ...allActionsAllowed };
  }
  return map;
}

/** Permission map keyed by entity type, self plus hierarchy descendants, computed from the item's membership. A system admin without a membership gets full permissions, as on the backend. */
export function enrichWithPermissions(item: EnrichableChannel, channelType: ChannelEntityType): EnrichableChannel {
  const membership = item.membership ?? null;
  const existing = item.can;

  const isSystemAdmin = useUserStore.getState().isSystemAdmin;
  const computed = membership
    ? computeCan(channelType, membership, policyMatrix)
    : isSystemAdmin
      ? computeSystemAdminCan(channelType)
      : {};

  if (!hasCanChanged(existing, computed)) return item;

  return { ...item, can: computed };
}
